import { Controller } from "@hotwired/stimulus"

// Draws a custom-report map with Leaflet (OpenStreetMap tiles). CustomReportMapRenderer#render_interactive
// emits the trusted, empty container we attach to:
//
//   <div class="cc-report-map" data-controller="report-map" data-report-map-spec-value="…">
//
// The spec is library-neutral — { type, title, points: [{lat, lng, label, value}], minValue, maxValue } —
// so swapping mapping libraries later is a change to this file alone; the server contract holds.
//
// Each point is a circle marker whose radius scales with its value column (sized bubbles, good for
// density like "customers by zip"); rows without a value get a fixed radius. Spec strings are set as
// text (never innerHTML), so report data cannot inject markup. Leaflet loads as a code-split dynamic
// import, fetched only on pages that show a map rather than weighing down the main bundle.

const FILL = "#2563eb"
const MAX_RADIUS = 24
const MIN_RADIUS = 6

export default class extends Controller {
  static values = { spec: Object }

  async connect() {
    try {
      const { default: L } = await import("leaflet")
      await import("leaflet/dist/leaflet.css")
      // A turbo navigation (or re-run) can disconnect us while the dynamic import is in flight;
      // bail rather than render onto a detached element (Leaflet would throw).
      if (!this.element.isConnected) return
      this.draw(L)
    } catch (error) {
      console.error("report-map: failed to render", error)
      this.element.textContent = "Unable to render map."
    }
  }

  disconnect() {
    // Tear the map fully down so a reconnect starts clean rather than orphaning the prior
    // instance's tile layer and resize listeners. Also drop the mount div itself — map.remove()
    // only empties the element Leaflet managed, so leaving it would stack a second (empty,
    // bordered) canvas on the next connect.
    this.map?.remove()
    this.map = undefined
    this.mount?.remove()
    this.mount = undefined
  }

  draw(L) {
    const points = this.specValue.points ?? []
    // Render into a dedicated child so Leaflet owns a clean element of its own.
    this.mount = document.createElement("div")
    this.mount.className = "cc-report-map__canvas"
    this.element.appendChild(this.mount)

    this.map = L.map(this.mount, { scrollWheelZoom: false })
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap contributors",
      maxZoom: 19,
    }).addTo(this.map)

    const latLngs = []
    for (const point of points) {
      const latLng = [point.lat, point.lng]
      latLngs.push(latLng)
      const marker = L.circleMarker(latLng, {
        radius: this.radiusFor(point.value),
        color: FILL,
        fillColor: FILL,
        fillOpacity: 0.5,
        weight: 1,
      }).addTo(this.map)
      const popup = this.popupFor(point)
      if (popup) marker.bindPopup(popup)
    }

    if (latLngs.length === 1) {
      this.map.setView(latLngs[0], 11)
    } else if (latLngs.length > 1) {
      this.map.fitBounds(latLngs, { padding: [24, 24] })
    }
  }

  // Scale a value to a bubble radius across the spec's [minValue, maxValue] range. Points with no
  // value (or a degenerate range where every value is equal) get the midpoint radius.
  radiusFor(value) {
    const { minValue, maxValue } = this.specValue
    if (value == null || minValue == null || maxValue == null || maxValue === minValue) {
      return (MIN_RADIUS + MAX_RADIUS) / 2
    }
    const fraction = (value - minValue) / (maxValue - minValue)
    return MIN_RADIUS + fraction * (MAX_RADIUS - MIN_RADIUS)
  }

  // Build the popup as text nodes so report data can never inject markup.
  popupFor(point) {
    const hasLabel = point.label != null && point.label !== ""
    const hasValue = point.value != null
    if (!hasLabel && !hasValue) return null

    const wrapper = document.createElement("div")
    if (hasLabel) {
      const label = document.createElement("strong")
      label.textContent = point.label
      wrapper.appendChild(label)
    }
    if (hasValue) {
      if (hasLabel) wrapper.appendChild(document.createElement("br"))
      const value = document.createElement("span")
      value.textContent = String(point.value)
      wrapper.appendChild(value)
    }
    return wrapper
  }
}
